import {HMDocument, HMDocumentInfo, HMExistingDraft, UnpackedHypermediaId} from '@seed-hypermedia/client/hm-types'
import {QuerySearchInputProvider} from '@shm/editor/query-search-context'
import {hmId, useJoinSite, useUniversalAppContext, useUniversalClient} from '@shm/shared'
import {CommentsProvider, InlineEditCommentProps} from '@shm/shared/comments-service-provider'
import {NOTIFY_SERVICE_HOST} from '@shm/shared/constants'
import {getDocumentTitle} from '@shm/shared/content'
import {DocumentActionsProvider} from '@shm/shared/document-actions-context'
import type {DocumentContentProps} from '@shm/shared/document-content-props'
import {canCreateChildDocuments} from '@shm/shared/document-utils'
import {useGatewayUrlStream} from '@shm/shared/gateway-url'
import {HomeDraftProvider} from '@shm/shared/home-draft-context'
import {isDocumentCardCleanupJobActive} from '@shm/shared/models/document-card-cleanup-machine'
import {type EditorAccessor} from '@shm/shared/models/document-machine'
import {useResource} from '@shm/shared/models/entity'
import {draftBindingSchemaDrafts, draftSchemaDraft} from '@shm/shared/models/schema-draft'
import {selectContext, useDocumentMachineRef, useOnDocumentRenamed} from '@shm/shared/models/use-document-machine'
import {QueryBlockDraftsProvider} from '@shm/shared/query-block-drafts-context'
import {replaceRouteDocumentId} from '@shm/shared/routes'
import {getDraftPlaceholderParentId} from '@shm/shared/utils/breadcrumbs'
import {useCommentNavigation} from '@shm/shared/utils/comment-navigation'
import type {DocumentCardActionOrigin} from '@shm/shared/utils/document-actions'
import {createWebHMUrl, latestId, unpackHmId} from '@shm/shared/utils/entity-id-url'
import {useNavRoute, useNavigate} from '@shm/shared/utils/navigation'
import {entityQueryPathToHmIdPath} from '@shm/shared/utils/path-api'
import {getDraftReturnParentId, isReservedLazyDraftId} from '@shm/shared/utils/reserved-draft-ids'
import {Button} from '@shm/ui/button'
import {createDocumentVersionsPanelRoute} from '@shm/ui/document-versions-panel'
import {EditingDocToolsRight, type EditingToolbarCallbacks} from '@shm/ui/editing-toolbar'
import {Trash} from '@shm/ui/icons'
import {InlineSubscribeBox} from '@shm/ui/inline-subscribe-box'
import {InspectorPage} from '@shm/ui/inspector-page'
import type {MenuItemType} from '@shm/ui/options-dropdown'
import {CommentEditorProps, ResourcePage} from '@shm/ui/resource-page-common'
import {Spinner} from '@shm/ui/spinner'
import {toast} from '@shm/ui/toast'
import {useAppDialog} from '@shm/ui/universal-dialog'
import {useQuery} from '@tanstack/react-query'
import {FileCode2, FileInput, Globe} from 'lucide-react'
import {blobBuilderMenuItems} from '@shm/ui/schema/blob-menu-items'
import {Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {
  EditProfileDialog,
  LogoutButton,
  useCreateAccount,
  useLocalKeyPair,
  useLocalKeyPairLoaded,
  useVaultSuccessDialog,
} from './auth'
import {preloadCommenting} from './client-lazy'
import {useWebCanEdit} from './document-edit/use-web-can-edit'
import {isPendingSpaceUid, isSpaceHomeDraftId} from './document-edit/web-create-space-draft'
import {createWebDocumentMachine} from './document-edit/web-document-actors'
import {
  enqueueWebDocumentCardCleanup,
  getWebDocumentCardCleanupSnapshot,
  loadWebCleanupDraft,
  reportWebDocumentCardCleanupConflict,
  startWebDocumentCardCleanupCoordinator,
  subscribeWebDraftExternallyModified,
} from './document-edit/web-document-card-cleanup'
import {WebDraftActionsProvider} from './document-edit/web-draft-actions-provider'
import {WebDraftBreadcrumbProvider} from './document-edit/web-draft-breadcrumb-provider'
import {cleanupOldWebDocDrafts, getLatestWebDocDraftForDoc, getWebDocDraft} from './document-edit/web-draft-db'
import {getWebDraftShellId, shouldUseLocalWebDraftShell} from './document-edit/web-draft-shell'
import {makeWebFileUpload} from './document-edit/web-image-upload'
import {WebQueryBlockDraftSlot} from './document-edit/web-query-block-draft-slot'
import {restoreWebDocumentVersion} from './document-edit/web-restore-document-version'
import {setPendingIntent} from './local-db'
import {PageFooter} from './page-footer'
import {processPendingIntent} from './pending-intent'
import {openWebDomainSettings} from './models/domain-publishing'
import {useCreateSpaceDialog, useHasExistingSpace, usePublishSpaceDraft} from './web-create-space-dialog'
import {useWebDeleteDocumentDialog} from './web-delete-document-dialog'
import {WebDocumentPrefetch} from './web-document-prefetch'
import {useWebDocumentDestinationDialog} from './web-move-document-dialog'
import {WebQuerySearchInput} from './web-query-search-input'
import {
  WebDocumentCreateButton,
  WebHeaderActions,
  WebSitePageShell,
  useWebCreateDocumentMenuItem,
  useWebMenuItems,
} from './web-utils'

/** Lazy-loaded inline comment editor — avoids pulling the full editor bundle eagerly. */
const LazyWebInlineEditor = lazy(() => import('./commenting').then((mod) => ({default: mod.WebInlineEditBox})))

/** Renders the inline editor for web comment editing, lazy-loaded. */
function renderWebInlineEditor(props: InlineEditCommentProps) {
  return (
    <Suspense fallback={<Spinner />}>
      <LazyWebInlineEditor {...props} />
    </Suspense>
  )
}

function WebDraftExternalModificationListener() {
  const actorRef = useDocumentMachineRef()

  useEffect(() => {
    const subscription = actorRef.on('maintenanceConflict', (event) => {
      Promise.resolve(reportWebDocumentCardCleanupConflict(event.jobId, event.error)).catch((error) => {
        console.error('Failed to record document maintenance conflict', error)
      })
    })
    return () => subscription.unsubscribe()
  }, [actorRef])

  useEffect(() => {
    return subscribeWebDraftExternallyModified(async (event) => {
      const context = selectContext(actorRef.getSnapshot())
      if (event.source !== 'document-card-cleanup' || event.draftId !== context.draftId) return
      const draft = await loadWebCleanupDraft(event.draftId)
      if (
        event.publishedDocument &&
        (!draft || [...draft.deps].sort().join('.') !== event.publishedDocument.version.split('.').sort().join('.'))
      )
        return

      if (event.maintenanceRevision !== undefined && draft?.maintenanceRevision !== event.maintenanceRevision) return
      actorRef.send({
        type: 'draft.externallyModified',
        jobId: event.jobId,
        cardBlockId: event.cardBlockId,
        childDraftId: event.childDraftId,
        targetBlockId: event.targetBlockId,
        operation: event.operation === 'delete-child' ? undefined : event.operation,
        sourceDocumentId: event.sourceDocumentId,
        targetDocumentId: event.targetDocumentId,
        previousContent: event.previousContent,
        publishedDocument: event.publishedDocument,
        draftId: event.draftId,
        source: event.source,
        deletedDocumentId: event.deletedDocumentId || event.sourceDocumentId,
        removedBlockIds: event.changedBlockIds,
        content: draft?.content ?? null,
        cursorPosition: draft?.cursorPosition ?? null,
        metadata: draft?.metadata ?? null,
        deps: draft?.deps ?? null,
        baseBlocks: draft?.baseBlocks ?? null,
        maintenanceRevision: draft?.maintenanceRevision ?? 0,
        removedChildDocumentIds: draft?.removedChildDocumentIds ?? null,
        mineTouchedIds: draft?.mineTouchedIds ?? null,
      })
    })
  }, [actorRef])

  return null
}

function WebDocumentRenameNavigator({onRenamed}: {onRenamed: (payload: {oldId: string; newId: string}) => void}) {
  useOnDocumentRenamed(onRenamed)
  return null
}

export interface WebResourcePageProps {
  docId: UnpackedHypermediaId
  CommentEditor?: React.ComponentType<CommentEditorProps>
  ssrContentHTML?: string | null
}

/**
 * Web-specific wrapper for ResourcePage that handles:
 * - HypermediaHostBanner (shown when viewing content from a different site)
 * - Account button with login/create account flow
 */
// Client-only wrapper: DocumentEditor uses BlockNoteView which requires
// window.matchMedia (DOM API unavailable during SSR).
function useClientDocumentEditor(): React.ComponentType<DocumentContentProps> | undefined {
  const [Component, setComponent] = useState<React.ComponentType<DocumentContentProps> | undefined>(undefined)
  useEffect(() => {
    import('@shm/editor/document-editor').then((mod) => {
      setComponent(() => mod.DocumentEditor)
    })
  }, [])
  return Component
}

export function WebResourcePage({docId, CommentEditor, ssrContentHTML}: WebResourcePageProps) {
  const DocumentContentComponent = useClientDocumentEditor()
  const {origin, originHomeId, experiments} = useUniversalAppContext()
  const route = useNavRoute()
  const navigate = useNavigate()
  const publishSpaceDraft = usePublishSpaceDraft()
  const replaceRoute = useNavigate('replace')
  const userKeyPair = useLocalKeyPair()
  const editProfileDialog = useAppDialog(EditProfileDialog)
  const vaultSuccessContent = useVaultSuccessDialog()
  const universalClient = useUniversalClient()
  const gwUrl = useGatewayUrlStream()
  const linkExtensionOptions = useMemo(() => ({universalClient, gwUrl}), [universalClient, gwUrl])
  const {canEdit, signingAccountId, capability, capabilitiesLoading} = useWebCanEdit(docId)
  const {createAccount, content: createAccountContent} = useCreateAccount()
  const keyPairLoaded = useLocalKeyPairLoaded()
  const fileUpload = useMemo(() => makeWebFileUpload(universalClient), [universalClient])

  // Building-block entry points (desktop shows these under Developer Mode; web
  // enables developerMode by default). "New Schema" is a new instance of the
  // built-in meta-schema. The editor's own menu offers these too once open.
  // Hidden when logged out: there is no key to publish the blob with.
  const schemaBuilderMenuItems = useMemo<MenuItemType[]>(
    () => (experiments?.developerMode && userKeyPair ? blobBuilderMenuItems(navigate) : []),
    [experiments?.developerMode, userKeyPair, navigate],
  )

  // Editor accessor — populated by the editor's onEditorReady callback.
  const editorRef = useRef<any>(null)
  const editorAccessor = useMemo<EditorAccessor>(
    () => ({
      getTopLevelBlocks: () => editorRef.current?.topLevelBlocks ?? null,
      getCursorPosition: () => editorRef.current?._tiptapEditor?.view?.state?.selection?.$anchor?.pos ?? null,
    }),
    [],
  )
  const onEditorReady = useCallback((editor: any) => {
    editorRef.current = editor
  }, [])

  // Post-publish: navigate to latest version so user sees published content
  const replaceRouteRef = useRef(replaceRoute)
  replaceRouteRef.current = replaceRoute
  const handleDocumentRenamed = useCallback(({newId}: {oldId: string; newId: string}) => {
    const newDocId = unpackHmId(newId)
    if (!newDocId) return
    replaceRouteRef.current({key: 'document', id: newDocId} as any)
  }, [])
  const routeRef = useRef(route)
  routeRef.current = route
  const onPublishSuccess = useCallback(
    (newDocument?: {account?: string; path?: string | null; metadata?: HMDocument['metadata']}) => {
      const publishedTitle =
        getDocumentTitle({metadata: newDocument?.metadata ?? {}, path: newDocument?.path ?? ''}) || 'Document'
      toast.success(`${publishedTitle} Published.`)
      const currentRoute = routeRef.current
      if (currentRoute.key === 'document') {
        const currentId = (currentRoute as any).id as UnpackedHypermediaId | undefined
        if (newDocument?.account) {
          const publishedId = hmId(newDocument.account, {
            path: entityQueryPathToHmIdPath(newDocument.path),
            latest: true,
          })
          if (currentId && publishedId.id !== currentId.id) {
            replaceRouteRef.current({...currentRoute, id: publishedId} as any)
            return
          }
        }
        if (currentId?.version) {
          replaceRouteRef.current({
            ...currentRoute,
            id: {...currentId, version: null},
          } as any)
        }
      } else if (currentRoute.key === 'site-profile') {
        const currentId = (currentRoute as any).id as UnpackedHypermediaId | undefined
        if (currentId?.version) {
          replaceRouteRef.current({
            ...currentRoute,
            id: {...currentId, version: null},
          } as any)
        }
      }
    },
    [],
  )

  const placeholderDraftId = useMemo(() => getWebDraftShellId(docId.path), [docId.path])

  // Load any local IDB draft for this doc. Placeholder draft URLs must load by
  // exact draft id instead of asking the backend for the nonexistent document.
  const draftQuery = useQuery({
    queryKey: ['web-doc-draft', docId.id, signingAccountId ?? null, placeholderDraftId ?? null] as const,
    queryFn: async () => {
      const isPending = isPendingSpaceUid(docId.uid)
      if (!signingAccountId && !isPending) return null
      if (placeholderDraftId) {
        const draft = await getWebDocDraft(placeholderDraftId)
        if (draft?.docId !== docId.id) return null
        if (draft.signingAccountId !== signingAccountId && !isPending) return null
        return draft
      }
      if (!canEdit) return null
      return getLatestWebDocDraftForDoc(docId.id)
    },
    enabled:
      typeof window !== 'undefined' &&
      (!!signingAccountId || isPendingSpaceUid(docId.uid)) &&
      (canEdit || !!placeholderDraftId),
    staleTime: 60_000,
    keepPreviousData: false,
  })
  // Keep local drafts even when their base is no longer latest. Remote updates
  // are handled by the document machine's queued rebase flow; deleting here can
  // destroy a valid in-progress draft when the user is merely viewing a pinned
  // historical version.
  const loadedDraftData = draftQuery.data
  const draftData =
    loadedDraftData &&
    (placeholderDraftId
      ? loadedDraftData.draftId === placeholderDraftId && loadedDraftData.docId === docId.id
      : loadedDraftData.docId === docId.id)
      ? loadedDraftData
      : null
  const isDraftStale = false

  const isPendingSpace = isPendingSpaceUid(docId.uid)
  // A create space home draft.
  const isSpaceHomeDraft = isSpaceHomeDraftId(placeholderDraftId)
  const canEditLocalPlaceholderDraft =
    !!placeholderDraftId &&
    !!draftData &&
    (isPendingSpace || (!!signingAccountId && draftData.signingAccountId === signingAccountId))
  const effectiveCanEdit =
    canEdit || canEditLocalPlaceholderDraft || (!!placeholderDraftId && (!!signingAccountId || isPendingSpace))
  const effectiveCapabilityCid = capability && capability.id !== '_owner' ? capability.id : draftData?.capabilityCid

  // Build a documentMachine wired to web actors. Stable per (docId, signing identity, capability).
  const machine = useMemo(() => {
    return createWebDocumentMachine({
      docId,
      getEditor: () => editorAccessor,
      client: universalClient,
      getSigner: (accountUid: string) => {
        if (!universalClient.getSigner) {
          throw new Error('Universal client does not provide a signer; cannot publish from web')
        }
        return universalClient.getSigner(accountUid)
      },
      getCapabilityCid: () => effectiveCapabilityCid,
      onPublishSuccess,
    })
  }, [docId.id, universalClient, editorAccessor, effectiveCapabilityCid, onPublishSuccess])

  useEffect(() => {
    if (typeof window === 'undefined') return
    startWebDocumentCardCleanupCoordinator({client: universalClient})
  }, [universalClient])

  // Mirror of the draftQuery `enabled` gate below — a draft could still be
  // loading whenever this is true.
  const draftQueryEnabled = typeof window !== 'undefined' && !!signingAccountId && (canEdit || !!placeholderDraftId)

  const existingDraft: HMExistingDraft | false | undefined = useMemo(() => {
    // CRITICAL: returning `false` here declares "no draft exists", which latches
    // the document machine's `draft.resolved` to the no-draft branch (it only
    // fires once). During the async startup window — identity still loading from
    // IndexedDB, or capability lookup not yet settled, or the draft query not yet
    // resolved — `effectiveCanEdit` is transiently false and `draftData` is
    // undefined. Returning `false` then would strand a saved draft (it loads a
    // moment later but the machine already resolved to "no draft"). Hold at
    // `undefined` (still loading) until each of those settles.
    if (!keyPairLoaded) return undefined
    if (!!signingAccountId && !canEdit && capabilitiesLoading) return undefined
    if (draftQueryEnabled && !draftQuery.isSuccess && !draftQuery.isError) return undefined
    if (!effectiveCanEdit) return false
    // Keep the current draft mounted during background refreshes. Autosave
    // invalidates this query, and treating that refetch as an initial load
    // temporarily replaced the editor with the draft-shell spinner.
    if (draftQuery.isLoading) return undefined
    const d = draftData
    if (!d || isDraftStale) return false
    return {id: d.draftId, metadata: d.metadata as HMExistingDraft['metadata']}
  }, [
    keyPairLoaded,
    signingAccountId,
    canEdit,
    capabilitiesLoading,
    draftQueryEnabled,
    draftQuery.isSuccess,
    draftQuery.isError,
    effectiveCanEdit,
    draftQuery.isLoading,
    draftData,
    isDraftStale,
  ])
  const reservedDraftId =
    placeholderDraftId && !draftData && isReservedLazyDraftId(placeholderDraftId) ? placeholderDraftId : null
  const useLocalDraftShell = shouldUseLocalWebDraftShell({
    placeholderDraftId,
    isDraftLoading: draftQuery.isInitialLoading,
    hasDraft: !!draftData && !isDraftStale,
    isReservedDraft: !!reservedDraftId,
  })
  const existingDraftContent = isDraftStale ? undefined : draftData?.content ?? undefined
  const existingDraftCursorPosition = isDraftStale ? undefined : draftData?.cursorPosition ?? undefined
  const existingDraftPublishPath = isDraftStale ? undefined : draftData?.publishPath ?? undefined
  const existingDraftSchemaDraft = isDraftStale ? undefined : draftSchemaDraft(draftData) ?? undefined
  const existingDraftBindingSchemaDrafts = isDraftStale ? undefined : draftBindingSchemaDrafts(draftData) ?? undefined

  // Garbage-collect old IDB drafts once per session.
  useEffect(() => {
    cleanupOldWebDocDrafts().catch((err) => console.warn('cleanupOldWebDocDrafts failed', err))
  }, [])

  // Determine if viewing own profile on site-profile page
  const isSiteProfile = route.key === 'site-profile'
  const profileAccountUid = isSiteProfile ? route.accountUid || docId.uid : null
  const ownAccountUid = userKeyPair?.delegatedAccountUid ?? userKeyPair?.id
  const isOwnProfile = isSiteProfile && !!userKeyPair && profileAccountUid === ownAccountUid
  const isDelegated = !!userKeyPair?.delegatedAccountUid

  // Profile edit callback - only for non-delegated own profile
  const onEditProfile = useMemo(() => {
    if (!isOwnProfile || isDelegated || !profileAccountUid) return undefined
    return () => editProfileDialog.open({accountUid: profileAccountUid})
  }, [isOwnProfile, isDelegated, profileAccountUid, editProfileDialog])

  // "Create a space" side panel, opened from the profile header.
  const {open: openCreateSpaceDialog, content: createSpaceDialogContent} = useCreateSpaceDialog()
  const {data: existingSpace} = useHasExistingSpace(isOwnProfile ? ownAccountUid : undefined, universalClient)
  const hasExistingSpace = !!existingSpace?.exists

  // Profile header buttons (account settings + logout + create space) - only for own profile.
  const profileHeaderButtons = useMemo(() => {
    if (!isOwnProfile) return undefined
    return (
      <>
        <LogoutButton />
        {hasExistingSpace ? null : (
          <Button variant="default" onClick={openCreateSpaceDialog}>
            Create my space
          </Button>
        )}
      </>
    )
  }, [isOwnProfile, hasExistingSpace, openCreateSpaceDialog])

  // Follow intent flow for unauthenticated users
  const {content: followAccountContent, createAccount: openFollowAccountDialog} = useCreateAccount({
    onClose: () => {
      processPendingIntent(originHomeId ?? undefined)
    },
  })

  const onFollowClick = useMemo(() => {
    if (userKeyPair) return undefined
    if (!isSiteProfile || !profileAccountUid) return undefined
    return async () => {
      await setPendingIntent({type: 'follow', profileUid: profileAccountUid})
      openFollowAccountDialog()
    }
  }, [userKeyPair, isSiteProfile, profileAccountUid, openFollowAccountDialog])

  // Preload the comment editor chunk on first hover over any Comments-related element
  const preloaded = useRef(false)
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (preloaded.current) return
      const target = e.target as HTMLElement
      if (target.closest?.('[data-tab="comments"]')) {
        preloaded.current = true
        preloadCommenting()
      }
    }
    document.addEventListener('mouseover', handler, {passive: true})
    return () => document.removeEventListener('mouseover', handler)
  }, [])

  // Shared toolbar callbacks for web
  const webToolbarCallbacks: EditingToolbarCallbacks = useMemo(
    () => ({
      getDocumentUrl: (id: UnpackedHypermediaId) =>
        createWebHMUrl(id.uid, {
          path: id.path,
          hostname: origin || null,
          originHomeId: originHomeId ?? undefined,
        }),
      onDiscardConfirm: (discardDraftId: string, send) => {
        if (window.confirm('Discard draft changes?')) {
          send({type: 'edit.discard'})
          const parentId = getDraftPlaceholderParentId(docId, discardDraftId) ?? getDraftReturnParentId(discardDraftId)
          if (parentId) {
            replaceRoute({
              ...(route.key === 'document' ? route : {key: 'document'}),
              id: parentId,
            } as any)
          }
          toast.success('Draft changes discarded')
        }
      },
      onGoToVersions: (id: UnpackedHypermediaId) => {
        navigate({
          key: 'document',
          id,
          panel: createDocumentVersionsPanelRoute(id),
        } as any)
      },
      // Pending space home drafts publish by first creating an account.
      // Stashes a publish-draft intent, then open the account dialog
      // and resume after the vault sign in.
      onPublishIntercept: () => {
        const isSpaceDraft = isSpaceHomeDraft || isPendingSpace
        if (!isSpaceDraft || !placeholderDraftId) return false
        if (userKeyPair) void publishSpaceDraft(placeholderDraftId)
        else {
          void setPendingIntent({type: 'publish-draft', draftId: placeholderDraftId}).then(() => {
            createAccount({source: 'join'})
          })
        }
        return true
      },
    }),
    [
      origin,
      originHomeId,
      navigate,
      publishSpaceDraft,
      replaceRoute,
      docId,
      route,
      isSpaceHomeDraft,
      isPendingSpace,
      placeholderDraftId,
      userKeyPair,
      createAccount,
    ],
  )

  const showPublishToolbar = route.key === 'document' || route.key === 'metadata'
  const currentResource = useResource(useLocalDraftShell ? undefined : docId)
  const currentDocument = currentResource.data?.type === 'document' ? currentResource.data.document : undefined

  const editingFloatingActions =
    effectiveCanEdit && showPublishToolbar
      ? ({menuItems}: {menuItems: any[]}) => (
          <EditingDocToolsRight
            docId={docId}
            existingMenuItems={menuItems}
            beforePublish={
              <WebDocumentCreateButton
                locationId={docId}
                currentDocument={currentDocument}
                canEdit={effectiveCanEdit}
                signingAccountId={signingAccountId ?? undefined}
                capabilityCid={effectiveCapabilityCid}
              />
            }
            {...webToolbarCallbacks}
          />
        )
      : undefined

  const siteUid = docId.uid
  const cleanupSnapshot = useQuery({
    queryKey: ['web-document-card-cleanup'],
    queryFn: () => getWebDocumentCardCleanupSnapshot(),
    refetchInterval: 1_000,
  })
  const fileBrowserOnIncludeDocument = useCallback(
    async (document: HMDocumentInfo, context: import('@shm/ui/site-file-browser').IncludeDocumentContext) => {
      try {
        const result = await enqueueWebDocumentCardCleanup(
          {
            operation: 'add',
            parentDocumentId: context.parentId.id,
            targetDocumentId: document.id.id,
            signingAccountUid: context.signingAccountUid,
            capabilityId: context.capabilityId,
          },
          {client: universalClient},
        )
        if (!result.enqueued && result.reason !== 'duplicate') throw new Error('Could not queue document inclusion')
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Could not include document')
        throw error
      }
    },
    [universalClient],
  )
  const fileBrowserGetIncludeDocumentState = useCallback(
    (document: HMDocumentInfo) => {
      const matchingJob = cleanupSnapshot.data?.jobs.find(
        (job) => job.operation === 'add' && job.target?.id === document.id.id,
      )
      return {
        disabled: false,
        pending: matchingJob ? isDocumentCardCleanupJobActive(matchingJob) : false,
        reason: matchingJob?.state === 'failedNeedsAttention' ? matchingJob.lastError : undefined,
      }
    },
    [cleanupSnapshot.data?.jobs],
  )
  const canCreateChildDocs =
    !!currentDocument?.version && canCreateChildDocuments(currentDocument?.visibility, draftData?.visibility)
  const {menuItem: fileBrowserCreateMenuItem} = useWebCreateDocumentMenuItem({
    locationId: hmId(docId.uid),
    signingAccountId: signingAccountId ?? undefined,
    canCreate: effectiveCanEdit && !!signingAccountId,
    capabilityCid: effectiveCapabilityCid,
  })
  const webMenuItems = useWebMenuItems(docId, {includeInspect: false})
  const deleteCapabilityId = capability && capability.id !== '_owner' ? capability.id : undefined
  const deleteDialog = useWebDeleteDocumentDialog({
    signingAccountId: signingAccountId ?? undefined,
    capabilityId: deleteCapabilityId,
    canDelete: effectiveCanEdit,
  })
  const destinationDialog = useWebDocumentDestinationDialog({
    signingAccountId: signingAccountId ?? undefined,
    capabilityId: deleteCapabilityId,
    writableLocationId: capability?.id === '_owner' ? hmId(docId.uid) : capability?.grantId,
    canMove: !!signingAccountId,
  })
  const onDeleteDocument = useCallback(
    (id: UnpackedHypermediaId, onSuccess?: () => void) => {
      deleteDialog.open({id, onSuccess})
    },
    [deleteDialog],
  )
  const onMoveDocument = useMemo(() => {
    if (!signingAccountId) return undefined
    return (id: UnpackedHypermediaId, origin?: DocumentCardActionOrigin) =>
      destinationDialog.open({id, mode: 'move', origin})
  }, [destinationDialog, signingAccountId])
  const onRepublishDocument = useMemo(() => {
    if (!signingAccountId) return undefined
    return (id: UnpackedHypermediaId, origin?: DocumentCardActionOrigin) =>
      destinationDialog.open({id, mode: 'republish', origin})
  }, [destinationDialog, signingAccountId])
  const canWriteDocument = useCallback(
    (id: UnpackedHypermediaId) =>
      !!signingAccountId && (id.uid === signingAccountId || (effectiveCanEdit && id.uid === docId.uid)),
    [docId.uid, effectiveCanEdit, signingAccountId],
  )
  const homeDraftOverride: boolean | undefined = isSpaceHomeDraft || isPendingSpace ? true : undefined
  const isHomeTarget = homeDraftOverride ?? !docId.path?.length
  const moveMenuItem = useMemo<MenuItemType | null>(() => {
    if (!effectiveCanEdit || !signingAccountId || isHomeTarget) return null
    return {
      key: 'move',
      label: 'Move',
      icon: <FileInput className="size-4" />,
      onClick: () => {
        const draftMoveId = draftData?.draftId || placeholderDraftId
        if (draftMoveId) {
          const fallbackParent = docId.path?.length ? hmId(docId.uid, {path: docId.path.slice(0, -1)}) : undefined
          destinationDialog.open({
            id: docId,
            mode: 'move',
            origin: draftData?.locationUid
              ? {parentDocumentId: hmId(draftData.locationUid, {path: draftData.locationPath ?? []})}
              : fallbackParent
                ? {parentDocumentId: fallbackParent}
                : undefined,
            draft: {
              draftId: draftMoveId,
              title: (draftData?.metadata as any)?.name,
              icon: (draftData?.metadata as any)?.icon,
            },
          })
          return
        }
        destinationDialog.open({id: docId, mode: 'move'})
      },
    }
  }, [destinationDialog, docId, draftData, effectiveCanEdit, isHomeTarget, placeholderDraftId, signingAccountId])
  const deleteMenuItem = useMemo<MenuItemType | null>(() => {
    if (!effectiveCanEdit || !signingAccountId || isHomeTarget) return null
    return {
      key: 'delete',
      label: 'Delete Document',
      icon: <Trash className="size-4" />,
      variant: 'destructive',
      onClick: () => {
        onDeleteDocument(docId, () => {
          replaceRoute({
            key: 'document',
            id: hmId(docId.uid, {path: docId.path?.slice(0, -1)}),
          } as any)
        })
      },
    }
  }, [docId, effectiveCanEdit, isHomeTarget, onDeleteDocument, replaceRoute, signingAccountId])
  const optionsMenuItems = useMemo(
    () =>
      [
        ...webMenuItems,
        isHomeTarget && effectiveCanEdit && signingAccountId === docId.uid && !placeholderDraftId
          ? {
              key: 'web-domain',
              label: 'Web Domain',
              icon: <Globe className="size-4" />,
              onClick: () => openWebDomainSettings(hmId(docId.uid), signingAccountId),
            }
          : null,
        moveMenuItem,
        deleteMenuItem,
        ...schemaBuilderMenuItems,
      ].filter(Boolean) as MenuItemType[],
    [
      deleteMenuItem,
      moveMenuItem,
      webMenuItems,
      schemaBuilderMenuItems,
      isHomeTarget,
      effectiveCanEdit,
      signingAccountId,
      docId.uid,
      placeholderDraftId,
    ],
  )

  // Inline subscribe box for non-members
  const {isJoined} = useJoinSite({siteUid})
  const siteResource = useResource(docId.path?.length ? undefined : docId)
  const siteMetadata = siteResource.data?.type === 'document' ? siteResource.data.document?.metadata : undefined
  // Only offer to subscribe on a published doc — not while previewing an
  // unpublished draft (placeholder route), where subscription can't work yet.
  const showSubscribeBox = (!userKeyPair || !isJoined) && !placeholderDraftId
  const inlineInsert = useMemo(() => {
    if (!showSubscribeBox || !NOTIFY_SERVICE_HOST) return undefined
    return (
      <InlineSubscribeBox
        accountId={siteUid}
        notifyServiceHost={NOTIFY_SERVICE_HOST}
        accountMeta={siteMetadata ?? undefined}
      />
    )
  }, [showSubscribeBox, siteUid, siteMetadata])

  const {onReplyClick, onReplyCountClick} = useCommentNavigation({
    docId,
    route,
    navigate,
    replaceRoute,
  })

  const onRestoreDocumentVersion = useCallback(
    async (id: UnpackedHypermediaId, selectedVersion: HMDocument) => {
      if (!effectiveCanEdit || !signingAccountId || !canWriteDocument(id)) {
        toast.error('You do not have permission to restore this document')
        return
      }
      if (!universalClient.getSigner) {
        toast.error('Restore is not available in this client')
        return
      }

      try {
        await restoreWebDocumentVersion(
          {
            targetId: id,
            selectedVersion,
            signerAccountUid: signingAccountId,
            capabilityCid: effectiveCapabilityCid,
          },
          {
            client: universalClient,
            getSigner: (accountUid) => universalClient.getSigner!(accountUid),
          },
        )
        replaceRouteRef.current(replaceRouteDocumentId(route, latestId(id)))
        toast.success('Document restored successfully')
      } catch (error) {
        console.error('Failed to restore document version:', error)
        toast.error(error instanceof Error ? error.message : 'Failed to restore document version')
        throw error
      }
    },
    [canWriteDocument, effectiveCanEdit, effectiveCapabilityCid, route, signingAccountId, universalClient],
  )

  const [lastCreatedDraftId, setLastCreatedDraftId] = useState<string | null>(null)
  const canCreateInlineDraft = !useLocalDraftShell && canCreateChildDocs

  return (
    <WebSitePageShell siteUid={docId.uid}>
      <CommentsProvider
        onReplyClick={onReplyClick}
        onReplyCountClick={onReplyCountClick}
        renderInlineEditor={renderWebInlineEditor}
      >
        <DocumentActionsProvider
          onCopyLink={() => {}}
          selectedAccountUid={signingAccountId ?? undefined}
          myAccountIds={signingAccountId ? [signingAccountId] : []}
          canWriteDocument={canWriteDocument}
          onMoveDocument={onMoveDocument}
          onRepublishDocument={onRepublishDocument}
          onDeleteDocument={onDeleteDocument}
          onRestoreDocumentVersion={effectiveCanEdit && signingAccountId ? onRestoreDocumentVersion : undefined}
        >
          <WebDraftActionsProvider
            canCreateInlineDraft={canCreateInlineDraft}
            signingAccountId={signingAccountId ?? undefined}
            capabilityCid={effectiveCapabilityCid}
          >
            <QueryBlockDraftsProvider
              DraftSlot={WebQueryBlockDraftSlot}
              lastCreatedDraftId={lastCreatedDraftId}
              setLastCreatedDraftId={setLastCreatedDraftId}
            >
              <QuerySearchInputProvider value={WebQuerySearchInput}>
                <WebDraftBreadcrumbProvider>
                  <HomeDraftProvider value={homeDraftOverride}>
                    <WebDocumentPrefetch originHomeId={originHomeId}>
                      {(onPrefetchDocument) => (
                        <ResourcePage
                          docId={docId}
                          resourceId={useLocalDraftShell ? null : docId}
                          CommentEditor={CommentEditor}
                          pageFooter={<PageFooter id={docId} />}
                          onEditProfile={onEditProfile}
                          profileHeaderButtons={profileHeaderButtons}
                          onFollowClick={onFollowClick}
                          rightActions={<WebHeaderActions siteUid={docId.uid} />}
                          optionsMenuItems={optionsMenuItems}
                          fileBrowserCreateMenuItem={fileBrowserCreateMenuItem}
                          fileBrowserOnIncludeDocument={fileBrowserOnIncludeDocument}
                          fileBrowserGetIncludeDocumentState={fileBrowserGetIncludeDocumentState}
                          inlineInsert={inlineInsert}
                          DocumentContentComponent={DocumentContentComponent}
                          ssrContentHTML={ssrContentHTML}
                          perspectiveAccountUid={ownAccountUid}
                          linkExtensionOptions={linkExtensionOptions}
                          canEdit={effectiveCanEdit}
                          machine={machine}
                          machineExtras={
                            <>
                              <WebDraftExternalModificationListener />
                              <WebDocumentRenameNavigator onRenamed={handleDocumentRenamed} />
                            </>
                          }
                          signingAccountId={signingAccountId ?? undefined}
                          publishAccountUid={signingAccountId ?? undefined}
                          onEditorReady={onEditorReady}
                          existingDraft={existingDraft}
                          reservedDraftId={reservedDraftId}
                          existingDraftVisibility={draftData?.visibility}
                          existingDraftContent={existingDraftContent}
                          existingDraftCursorPosition={existingDraftCursorPosition}
                          existingDraftPublishPath={existingDraftPublishPath}
                          existingDraftSchemaDraft={existingDraftSchemaDraft}
                          existingDraftBindingSchemaDrafts={existingDraftBindingSchemaDrafts}
                          existingDraftDeps={draftData?.deps}
                          existingDraftBaseBlocks={draftData?.baseBlocks ?? undefined}
                          existingDraftMaintenanceRevision={draftData?.maintenanceRevision}
                          existingDraftRemovedChildDocumentIds={draftData?.removedChildDocumentIds}
                          existingDraftMineTouchedIds={draftData?.mineTouchedIds}
                          draftVersionOnDiscardConfirm={webToolbarCallbacks.onDiscardConfirm}
                          editingFloatingActions={editingFloatingActions}
                          fileUpload={fileUpload}
                          onPrefetchDocument={onPrefetchDocument}
                        />
                      )}
                    </WebDocumentPrefetch>
                  </HomeDraftProvider>
                </WebDraftBreadcrumbProvider>
              </QuerySearchInputProvider>
            </QueryBlockDraftsProvider>
          </WebDraftActionsProvider>
        </DocumentActionsProvider>
      </CommentsProvider>
      {editProfileDialog.content}
      {followAccountContent}
      {createSpaceDialogContent}
      {createAccountContent}
      {deleteDialog.content}
      {destinationDialog.content}
      {vaultSuccessContent}
    </WebSitePageShell>
  )
}

/** Web-specific wrapper for the dedicated inspector page. */
export function WebInspectorPage({docId}: {docId: UnpackedHypermediaId}) {
  return (
    <WebSitePageShell siteUid={docId.uid}>
      <InspectorPage docId={docId} pageFooter={<PageFooter id={docId} />} />
    </WebSitePageShell>
  )
}
